import React from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { type AssetDto } from 'src/store/generated/leela-api-generated';
import { approvalIcon, SingleAssetList } from './AssetList';
import { capitalizeFirstLetter } from 'src/utils';

export interface AssetDetailApprovalModalModalProps {
  action: string;
  asset: AssetDto | undefined;
  isLoading?: boolean;
  open: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  // productStrategyId → display name; the confirm preview shows which single strategy row is written.
  strategyLabels?: Map<string, string>;
}

export const AssetDetailApprovalModal = (
  props: AssetDetailApprovalModalModalProps,
) => {
  const { action, asset, isLoading = false, open, onCancel, onConfirm, strategyLabels } = props;
  const { t } = useTranslation();

  return (
    <Modal
      data-testid="asset-detail-action-confirmation-modal"
      open={open}
      scrollContainer="viewport"
      title={t('header_asset_status_update_modal')}
      width="800px"
    >
      {asset && (
        <Column alignmentHorizontal="start" width="100%">
          <Row>
              {approvalIcon(action)}
              <Text type="b300">
                {t('modal_single_asset_approval_confirm_header', {
                  status: capitalizeFirstLetter(action),
                })}
              </Text>
            </Row>
          <SingleAssetList assets={[asset]} strategyLabels={strategyLabels} />
        </Column>
      )}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          {isLoading && (
            <Row
              data-testid="assset-action-loader"
              spacingInset="none 300"
              wrap="none"
            >
              <Loader size="medium" />
              <Text>{t('loader_loading')}</Text>
            </Row>
          )}

          <Button
            data-testid="cancel-asset-action"
            disabled={isLoading}
            size="small"
            type="secondary"
            onClick={onCancel}
          >
            {t('buttons_cancel')}
          </Button>
          <Button
            data-testid="confirm-asset-action"
            disabled={isLoading}
            size="small"
            type="primary"
            onClick={onConfirm}
          >
            {t('buttons_confirm')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
